import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Landing.css';
import '../components/Navbar.css';
import WinnerTicker from '../components/WinnerTicker';
import { track, EVENTS } from '../analytics';

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How do EZGAME tournaments work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Pay a $1 USDC entry fee, play one ranked Apex Legends or Fortnite match during the 30-minute window. The player with the most kills wins 90% of the prize pool, paid instantly on Polygon.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does it cost to enter a tournament?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Entry fees start at $1 USDC. Gas on Polygon is under $0.01 — you\'ll need a small amount of POL in your wallet to cover it.',
      },
    },
    {
      '@type': 'Question',
      name: 'How fast do winners get paid?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Payouts are instant and automatic. As soon as the tournament window closes and a winner is verified, the smart contract sends USDC directly to the winner\'s wallet — no waiting, no manual processing.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which games are supported?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'EZGAME supports Apex Legends, Fortnite, CS2 Premier, and Tarkov Arena tournaments. CS2 and Tarkov lobbies open April 27 alongside the 100-player lobbies.',
      },
    },
    {
      '@type': 'Question',
      name: 'What wallet do I need?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You need MetaMask or any WalletConnect-compatible wallet funded with USDC on the Polygon network.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is EZGAME available worldwide?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Anyone with a crypto wallet can participate regardless of location. No bank account or payment processor required.',
      },
    },
  ],
};

// ── Countdown target ─────────────────────────────────────────────────────────
// This counts down to the 100-player lobby opening.
// Mainnet is already live with the 50-player Fortnite beta.
const LAUNCH_TARGET = new Date('2026-04-27T20:00:00Z');

// ── Event JSON-LD schema (rich snippet for the 100-player lobby opening) ───
const EVENT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: 'EZGAME 100-Player Lobby Opening — Apex, Fortnite, CS2 & Tarkov',
  description: 'EZGAME opens its first 100-player lobbies. $1 USDC entry, 90% prize pool paid instantly on Polygon. Beta 50-player Fortnite tournaments are already live on mainnet.',
  startDate: '2026-04-27T20:00:00Z',
  endDate: '2026-04-27T21:00:00Z',
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
  location: {
    '@type': 'VirtualLocation',
    url: 'https://www.ezegames.live/lobby',
  },
  image: 'https://www.ezegames.live/og-image.png',
  organizer: {
    '@type': 'Organization',
    name: 'EZGAME',
    url: 'https://www.ezegames.live',
  },
  offers: {
    '@type': 'Offer',
    url: 'https://www.ezegames.live/',
    price: '1.00',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    validFrom: '2026-03-01T00:00:00Z',
  },
};

// HowTo schema — triggers Google's "How to" rich cards in search results
const HOWTO_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Enter an EZGAME Tournament and Win USDC',
  description: 'Enter a $1 Apex Legends or Fortnite kill tournament and get paid in USDC on Polygon if you win.',
  totalTime: 'PT35M',
  estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '1' },
  tool: [
    { '@type': 'HowToTool', name: 'MetaMask or WalletConnect wallet' },
    { '@type': 'HowToTool', name: 'USDC on Polygon (minimum $1)' },
  ],
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Connect your wallet',
      text: 'Open EZGAME and click "Connect Wallet". Use MetaMask or any WalletConnect wallet funded with at least $1 USDC on Polygon.',
      url: 'https://www.ezegames.live/',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Enter your game username',
      text: 'Type your Epic Games username (Fortnite) or EA/Origin username (Apex). This links your wallet to your in-game identity.',
      url: 'https://www.ezegames.live/',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Join an open tournament',
      text: 'Pick an open lobby and confirm the $1 USDC entry fee in MetaMask. Your spot is locked on-chain immediately.',
      url: 'https://www.ezegames.live/lobby',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Play one ranked match',
      text: 'During the 30-minute window, play one ranked match in Apex or Fortnite. The player with the most kills in the tournament pool wins.',
      url: 'https://www.ezegames.live/lobby',
    },
    {
      '@type': 'HowToStep',
      position: 5,
      name: 'Collect your winnings',
      text: 'The smart contract auto-verifies kills and sends 90% of the prize pool to the winner\'s wallet in seconds. No withdrawal needed.',
      url: 'https://www.ezegames.live/results',
    },
  ],
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function useLaunchCountdown() {
  const [parts, setParts] = useState({ d: 0, h: 0, m: 0, s: 0, launched: false });

  useEffect(() => {
    function tick() {
      const diff = LAUNCH_TARGET.getTime() - Date.now();
      if (diff <= 0) {
        setParts({ d: 0, h: 0, m: 0, s: 0, launched: true });
        return;
      }
      setParts({
        d: Math.floor(diff / 86_400_000),
        h: Math.floor((diff % 86_400_000) / 3_600_000),
        m: Math.floor((diff % 3_600_000) / 60_000),
        s: Math.floor((diff % 60_000) / 1_000),
        launched: false,
      });
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return parts;
}

function pad(n) { return String(n).padStart(2, '0'); }

const LEGENDS = [
  {
    name: 'Wraith',
    role: 'Skirmisher',
    tip: 'Phase out of danger and reposition for more kills.',
    color: '#8B00FF',
    img: '/images/wraitapexenh.png',
    fallbackGradient: 'linear-gradient(135deg, #2a0050, #8B00FF44)',
  },
  {
    name: 'Pathfinder',
    role: 'Skirmisher',
    tip: 'Grapple repositioning keeps you in fights longer.',
    color: '#00D4FF',
    img: '/images/pathfinderapexenh.png',
    fallbackGradient: 'linear-gradient(135deg, #002a40, #00D4FF44)',
  },
  {
    name: 'Horizon',
    role: 'Skirmisher',
    tip: 'Black Hole pulls enemies together for multi-kills.',
    color: '#FF006E',
    img: '/images/horizonapex.png',
    fallbackGradient: 'linear-gradient(135deg, #40001a, #FF006E44)',
  },
  {
    name: 'Bloodhound',
    role: 'Recon',
    tip: 'Track enemies through walls and hunt them down.',
    color: '#FF6B00',
    img: '/images/bloodhoundapex.png',
    fallbackGradient: 'linear-gradient(135deg, #401a00, #FF6B0044)',
  },
];

const FN_SKINS = [
  {
    name: 'Jonesy',
    role: 'OG',
    tip: 'Land hot, fight early, stack kills before the storm.',
    color: '#1DBEFF',
    img: '/images/fortnite/jonesy.png',
    fallback: 'linear-gradient(135deg, #001a2e, #1DBEFF44)',
  },
  {
    name: 'Midas',
    role: 'Legendary',
    tip: 'Every kill is golden — control the zone, control the game.',
    color: '#FFDE00',
    img: '/images/fortnite/midas.png',
    fallback: 'linear-gradient(135deg, #2a2000, #FFDE0044)',
  },
  {
    name: 'Peely',
    role: 'Fan Favourite',
    tip: "Don't underestimate the banana — he always comes back.",
    color: '#FFB800',
    img: '/images/fortnite/peely.png',
    fallback: 'linear-gradient(135deg, #2a1a00, #FFB80044)',
  },
  {
    name: 'Raven',
    role: 'Mythic',
    tip: 'Strike from the shadows, vanish before they respawn.',
    color: '#9B4DCA',
    img: '/images/fortnite/raven.png',
    fallback: 'linear-gradient(135deg, #1a0030, #9B4DCA44)',
  },
];

const ARTICLES = [
  {
    slug: 'fortnite-chapter-7-season-2-guide',
    tag: 'Fortnite',
    title: 'Fortnite Chapter 7 Season 2 "Showdown": New Map, Weapons & Everything You Need to Know',
    excerpt: 'New POIs, a reworked loot pool, and the Team Foundation vs Ice King war — full breakdown of Season 2.',
  },
  {
    slug: 'fncs-major-1-2026-preview',
    tag: 'Fortnite',
    title: 'FNCS Major 1 2026: $1M Prize Pool, Dates & How to Watch',
    excerpt: 'Heats begin April 18. The $1M LAN summit lands in Düsseldorf on May 30-31. Here\'s the full format and qualification path.',
  },
  {
    slug: 'best-fortnite-weapons-chapter-7-season-2',
    tag: 'Fortnite',
    title: 'Best Fortnite Weapons in Chapter 7 Season 2: Full Tier List',
    excerpt: 'Deadeye AR, Iron Pump, Vector 7 DMR — the meta has settled. Here\'s every weapon ranked for April 2026.',
  },
];

// ── Public waitlist feed ───────────────────────────────────────────────────
function PublicWaitlist() {
  const [entries, setEntries] = useState([]);
  const [total,   setTotal]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    function load() {
      fetch(`${API_URL}/waitlist/public?limit=50`)
        .then(r => r.json())
        .then(d => {
          if (cancelled) return;
          setEntries(d.entries || []);
          setTotal(d.total || 0);
        })
        .catch(() => {});
    }
    load();
    const id = setInterval(load, 20_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (entries.length === 0) return null;

  return (
    <div className="pub-waitlist">
      <div className="pub-waitlist__header">
        <div className="pub-waitlist__title">
          🔥 {total != null ? total.toLocaleString() : '—'} Gamers In Line
        </div>
        <div className="pub-waitlist__sub">
          These players are waiting for the 100-player lobby — are you on the list?
        </div>
      </div>
      <div className="pub-waitlist__grid">
        {entries.map((e, i) => (
          <div key={e.id ?? i} className={`pub-waitlist__card pub-waitlist__card--${e.game || 'fortnite'}`}>
            <span className="pub-waitlist__icon">
              {e.game === 'apex' ? '🎯' : e.game === 'cs2' ? '🔫' : e.game === 'tarkov' ? '☠️' : '🏗️'}
            </span>
            <span className="pub-waitlist__handle">{e.handle}</span>
            <span className="pub-waitlist__game">
              {e.game === 'apex' ? 'Apex' : e.game === 'cs2' ? 'CS2' : e.game === 'tarkov' ? 'Tarkov' : 'Fortnite'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tournament request queue widget ────────────────────────────────────────
function QueueWidget() {
  const [handle,  setHandle]  = useState('');
  const [game,    setGame]    = useState('fortnite');
  const [status,  setStatus]  = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');
  const [position, setPosition] = useState(null);
  const [total,    setTotal]    = useState(null);
  const [queueCounts, setQueueCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    function load() {
      fetch(`${API_URL}/queue/count`)
        .then(r => r.json())
        .then(d => { if (!cancelled) setQueueCounts(d); })
        .catch(() => {});
    }
    load();
    const id = setInterval(load, 15_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    if (status === 'loading' || !handle.trim()) return;
    setStatus('loading');
    try {
      const body = { game };
      if (game === 'fortnite') body.epic_player_id = handle.trim();
      else if (game === 'cs2') body.steam_player_id = handle.trim();
      else if (game === 'tarkov') body.tarkov_player_id = handle.trim();
      else body.apex_player_id = handle.trim();

      const res = await fetch(`${API_URL}/queue/join`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not join queue');
      setPosition(data.position);
      setTotal(data.total);
      setStatus('success');
      setMessage(data.alreadyQueued
        ? `You're already queued at position #${data.position}.`
        : `You're #${data.position} in the queue! We'll open a lobby when enough players request one.`
      );
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    }
  }

  const fnCount     = queueCounts?.byGame?.find(b => b.game === 'fortnite')?.c || 0;
  const apexCount   = queueCounts?.byGame?.find(b => b.game === 'apex')?.c || 0;
  const cs2Count    = queueCounts?.byGame?.find(b => b.game === 'cs2')?.c || 0;
  const tarkovCount = queueCounts?.byGame?.find(b => b.game === 'tarkov')?.c || 0;
  const totalQ      = queueCounts?.total || 0;

  const handlePlaceholder =
    game === 'fortnite' ? 'Epic username' :
    game === 'cs2'      ? 'Steam ID (e.g. 76561198XXXXXXXXX)' :
    game === 'tarkov'   ? 'Tarkov Arena username' :
                          'EA / Origin username';

  return (
    <div className="queue-widget">
      <div className="queue-widget__header">
        <div className="queue-widget__badge">🎮 Request a Tournament</div>
        <h3 className="queue-widget__title">Can't wait for launch?</h3>
        <p className="queue-widget__sub">
          Join the queue and we'll open a beta lobby as soon as enough players request one.
          First in queue = first notified.
        </p>
        {totalQ > 0 && (
          <div className="queue-widget__counts">
            <span className="queue-widget__count queue-widget__count--fn">
              🏗️ {fnCount} Fortnite
            </span>
            <span className="queue-widget__count queue-widget__count--apex">
              🎯 {apexCount} Apex
            </span>
            {cs2Count > 0 && (
              <span className="queue-widget__count queue-widget__count--cs2">
                🔫 {cs2Count} CS2
              </span>
            )}
            {tarkovCount > 0 && (
              <span className="queue-widget__count queue-widget__count--tarkov">
                ☠️ {tarkovCount} Tarkov
              </span>
            )}
          </div>
        )}
      </div>

      {status === 'success' ? (
        <div className="queue-widget__success">
          <div className="queue-widget__success-icon">✅</div>
          <p>{message}</p>
        </div>
      ) : (
        <form className="queue-widget__form" onSubmit={onSubmit}>
          <div className="queue-widget__row">
            <select
              className="waitlist__select"
              value={game}
              onChange={e => { setGame(e.target.value); setHandle(''); }}
              aria-label="Game"
            >
              <option value="fortnite">Fortnite</option>
              <option value="apex">Apex Legends</option>
              <option value="cs2">CS2 Premier</option>
              <option value="tarkov">Tarkov Arena</option>
            </select>
            <input
              className="waitlist__input"
              type="text"
              required
              placeholder={handlePlaceholder}
              value={handle}
              onChange={e => setHandle(e.target.value)}
            />
            <button
              type="submit"
              className="hero__cta-primary waitlist__submit"
              disabled={status === 'loading' || !handle.trim()}
            >
              {status === 'loading' ? 'Joining…' : 'Join Queue →'}
            </button>
          </div>
          {status === 'error' && <p className="waitlist__error">{message}</p>}
        </form>
      )}
    </div>
  );
}

// ── Waitlist form ─────────────────────────────────────────────────────────
function WaitlistForm({ launched }) {
  const [email, setEmail]     = useState('');
  const [handle, setHandle]   = useState('');
  const [game, setGame]       = useState('fortnite');
  const [status, setStatus]   = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');
  const [position, setPosition] = useState(null);
  const [total, setTotal]       = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [promoCode,    setPromoCode]    = useState(null);
  const [refCount,     setRefCount]     = useState(0);
  const [copied,       setCopied]       = useState(false);

  // Load the live waitlist count on mount so we can show social proof.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/waitlist/count`)
      .then(r => r.json())
      .then(data => { if (!cancelled && typeof data.total === 'number') setTotal(data.total); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // After success, poll how many people joined through the user's ref link
  useEffect(() => {
    if (status !== 'success' || !referralCode) return;
    let cancelled = false;
    function load() {
      fetch(`${API_URL}/waitlist/referrals/${referralCode}`)
        .then(r => r.json())
        .then(d => { if (!cancelled) setRefCount(d.count || 0); })
        .catch(() => {});
    }
    load();
    const id = setInterval(load, 15_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [status, referralCode]);

  async function onSubmit(e) {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    setMessage('');
    try {
      const storedRef = typeof localStorage !== 'undefined' ? localStorage.getItem('ezgame_ref') : null;
      const body = {
        email: email.trim(),
        preferred_game: game,
        ref: storedRef || undefined,
      };
      if (game === 'fortnite') body.epic_player_id = handle.trim();
      else if (game === 'cs2') body.steam_player_id = handle.trim();
      else if (game === 'tarkov') body.tarkov_player_id = handle.trim();
      else body.apex_player_id = handle.trim();

      const res = await fetch(`${API_URL}/waitlist/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not join waitlist');
      setPosition(data.position);
      setTotal(data.total);
      setReferralCode(data.referralCode || null);
      setPromoCode(data.promoCode || null);
      setStatus('success');
      setMessage(data.alreadyJoined ? 'You\'re already on the waitlist.' : 'You\'re in! We\'ll email you when the gates open.');
      if (!data.alreadyJoined) {
        track(EVENTS.WAITLIST_JOIN, {
          position: data.position,
          game,
          has_ref: !!storedRef,
        });
      }
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    }
  }

  function copyRefLink() {
    if (!referralCode) return;
    const link = `https://www.ezegames.live/?ref=${referralCode}`;
    try {
      navigator.clipboard.writeText(link);
      setCopied(true);
      track(EVENTS.REF_LINK_COPY, { code: referralCode });
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  }

  if (launched) return null;

  if (status === 'success') {
    const refLink  = referralCode ? `https://www.ezegames.live/?ref=${referralCode}` : 'https://www.ezegames.live/';
    const shareText = encodeURIComponent(
      `I just locked my spot on the @EZ_Egames waitlist 🎮⚡  $1 entry · 90% prize pool · instant USDC payouts. Mainnet launches in hours — refer 3 friends, earn 2% of every entry they play. Join me:`
    );
    const shareUrl = encodeURIComponent(refLink);
    const unlocked = refCount >= 3;

    return (
      <div className="waitlist waitlist--success">
        <div className="waitlist__badge">✅ You're in</div>
        <h3 className="waitlist__title">
          {position != null ? `#${position}` : '—'} on the waitlist
        </h3>
        <p className="waitlist__sub">{message}</p>
        {total != null && (
          <p className="waitlist__count">{total.toLocaleString()} gamers already queued up.</p>
        )}

        {promoCode && (
          <div className="waitlist__perk">
            <div className="waitlist__perk-label">🎁 Your free-entry code</div>
            <code className="waitlist__code">{promoCode}</code>
            <div className="waitlist__perk-sub">Redeem in Discord on launch day for one free $1 tournament entry.</div>
          </div>
        )}

        {referralCode && (
          <div className="waitlist__perk waitlist__perk--ref">
            <div className="waitlist__perk-label">💸 Earn 2% of friends' entries</div>
            <div className="waitlist__ref-link-row">
              <code className="waitlist__code waitlist__code--link">{refLink}</code>
              <button type="button" className="waitlist__copy" onClick={copyRefLink}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="waitlist__progress">
              <div className="waitlist__progress-track">
                <div
                  className="waitlist__progress-fill"
                  style={{ width: `${Math.min(100, (refCount / 3) * 100)}%` }}
                />
              </div>
              <div className="waitlist__progress-label">
                {unlocked
                  ? `🔓 Unlocked — earning 2% from ${refCount} friend${refCount === 1 ? '' : 's'}`
                  : `${refCount} / 3 friends joined — refer ${3 - refCount} more to unlock rev-share`}
              </div>
            </div>
          </div>
        )}

        <div className="waitlist__share-row">
          <a
            className="hero__cta-primary"
            href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Share on X →
          </a>
          <a
            className="hero__cta-secondary"
            href="https://discord.gg/smgDF9jH"
            target="_blank"
            rel="noopener noreferrer"
          >
            Join Discord
          </a>
        </div>
      </div>
    );
  }

  return (
    <form className="waitlist" onSubmit={onSubmit}>
      <div className="waitlist__header">
        <div className="waitlist__badge">🏆 100-Player Lobby Waitlist</div>
        <h3 className="waitlist__title">Reserve your spot in the 100-player lobby</h3>
        <p className="waitlist__sub">
          Beta 50-player Fortnite matches are live now. Join the waitlist to get notified first when the 100-player lobbies open April 27.
        </p>
        {total != null && (
          <p className="waitlist__count">
            🎮 {total.toLocaleString()} gamers already in line
          </p>
        )}
      </div>

      <div className="waitlist__row">
        <select
          className="waitlist__select"
          value={game}
          onChange={e => setGame(e.target.value)}
          aria-label="Preferred game"
        >
          <option value="fortnite">Fortnite</option>
          <option value="apex">Apex Legends</option>
          <option value="cs2">CS2 Premier</option>
          <option value="tarkov">Tarkov Arena</option>
        </select>
        <input
          className="waitlist__input"
          type="text"
          required
          placeholder={game === 'fortnite' ? 'Epic username' : game === 'cs2' ? 'Steam64 ID' : game === 'tarkov' ? 'Tarkov Arena username' : 'EA / Origin username'}
          value={handle}
          onChange={e => setHandle(e.target.value)}
          aria-label="In-game username"
        />
      </div>
      <div className="waitlist__row">
        <input
          className="waitlist__input"
          type="email"
          required
          placeholder="your@email.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          aria-label="Email address"
        />
        <button
          type="submit"
          className="hero__cta-primary waitlist__submit"
          disabled={status === 'loading'}
        >
          {status === 'loading' ? 'Joining...' : 'Claim My Spot →'}
        </button>
      </div>

      {status === 'error' && <p className="waitlist__error">{message}</p>}
      <p className="waitlist__legal">
        No spam. We'll only email you when tournaments go live.
      </p>
    </form>
  );
}

export default function Landing() {
  const countdown = useLaunchCountdown();

  return (
    <main className="landing">
      <Helmet>
        <title>EZGAME | Apex, Fortnite, CS2 &amp; Tarkov Tournaments — Win USDC Instantly</title>
        <meta name="description" content="$1 entry fee. Play one match. Win 90% of the prize pool paid instantly in USDC on Polygon. Apex Legends, Fortnite, CS2 and Tarkov Arena crypto tournaments with zero gas fees." />
        <link rel="canonical" href="https://www.ezegames.live/" />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="EZGAME" />
        <meta property="og:locale"      content="en_US" />
        <meta property="og:title"       content="EZGAME | Kill Tournaments — Win USDC Instantly" />
        <meta property="og:description" content="$1 entry. One match. 90% prize pool paid instantly on-chain. Apex, Fortnite, CS2 &amp; Tarkov Arena." />
        <meta property="og:url"         content="https://www.ezegames.live/" />
        <meta property="og:image"       content="https://www.ezegames.live/og-image.png" />
        <meta property="og:image:width"  content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:site"        content="@EZ_Egames" />
        <meta name="twitter:title"       content="EZGAME | Kill Tournaments — Win USDC Instantly" />
        <meta name="twitter:description" content="$1 entry. One match. 90% prize pool paid instantly on-chain. Apex, Fortnite, CS2 &amp; Tarkov." />
        <meta name="twitter:image"       content="https://www.ezegames.live/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(EVENT_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(HOWTO_SCHEMA)}</script>
      </Helmet>

      {/* ── Navbar ───────────────────────────────── */}
      <nav className="navbar" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>
        <Link to="/" className="navbar__logo">
          <span className="navbar__logo-text">EzEgames</span>
          <span className="navbar__logo-underline" />
        </Link>
        <div className="navbar__links">
          <Link to="/lobby"       className="navbar__link">Lobby</Link>
          <Link to="/results"     className="navbar__link">Results</Link>
          <Link to="/leaderboard" className="navbar__link">Leaderboard</Link>
          <Link to="/blog"        className="navbar__link">Blog</Link>
          <Link to="/profile"     className="navbar__link">My Profile</Link>
          <a
            href="https://discord.gg/smgDF9jH"
            target="_blank"
            rel="noopener noreferrer"
            className="navbar__link"
          >
            Discord
          </a>
        </div>
        <div className="navbar__right">
          <a href="#tournaments" className="hero__cta-primary" style={{ padding: '8px 20px', fontSize: 13 }}>
            Play Now
          </a>
        </div>
      </nav>
      <div style={{ height: 64 }} />

      {/* ── Live winner ticker (renders nothing pre-launch) ─ */}
      <WinnerTicker />

      {/* ── Hero ─────────────────────────────────── */}
      <section className="hero">
        <div className="hero__bg-image" />
        <div className="hero__overlay" />
        <div className="hero__glow" />
        <div className="hero__scanlines" />

        <div className="hero__content">
          <span className="hero__eyebrow">🔴 BETA LIVE ON MAINNET · Polygon · USDC</span>

          <h1 className="hero__title">
            One Match. One Winner.<br />
            <span className="gradient-text">Instant USDC.</span>
          </h1>

          <p className="hero__sub">
            Pay $1 USDC, play one match in Apex Legends, Fortnite, CS2 Premier, or Tarkov Arena, and get paid instantly on-chain.
            90% of the prize pool goes straight to the winner — instant payout, no delays, no middlemen.
          </p>

          <div className="hero__cta-row">
            {countdown.launched ? (
              <a href="#tournaments" className="hero__cta-primary">Choose Your Game →</a>
            ) : (
              <Link to="/lobby" className="hero__cta-primary">Enter Beta Lobby →</Link>
            )}
            <Link to="/blog" className="hero__cta-secondary">How It Works</Link>
          </div>

          <div className="hero__badges">
            <span className="hero__badge">🔒 Funds locked on-chain</span>
            <span className="hero__badge">⚡ Instant USDC payout</span>
            <span className="hero__badge">🎮 Apex · Fortnite · CS2 · Tarkov</span>
          </div>
        </div>

        {/* Floating character art */}
        <div className="hero__character-wrap">
          <img
            className="hero__character-img"
            src="https://www.ea.com/content/dam/apex-legends/images/2023/02/algs-split2-apex-legends-16x9.jpg"
            alt="Apex Legends"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div className="hero__character-glow" />
        </div>
      </section>

      {/* ── Launch countdown ─────────────────────── */}
      {!countdown.launched ? (
        <div className="launch-countdown" id="waitlist">
          <div className="launch-countdown__label">🏆 100-Player Lobby Opens In</div>
          <p style={{ color: '#00ff8c', fontSize: 13, margin: '0 0 12px', textAlign: 'center' }}>
            Beta 50-player Fortnite tournaments are <strong>live now on mainnet</strong> — enter the lobby below.
          </p>
          <div className="launch-countdown__timer">
            {countdown.d > 0 && (
              <div className="launch-countdown__unit">
                <span className="launch-countdown__num">{countdown.d}</span>
                <span className="launch-countdown__sub">days</span>
              </div>
            )}
            <div className="launch-countdown__unit">
              <span className="launch-countdown__num">{pad(countdown.h)}</span>
              <span className="launch-countdown__sub">hrs</span>
            </div>
            <div className="launch-countdown__sep">:</div>
            <div className="launch-countdown__unit">
              <span className="launch-countdown__num">{pad(countdown.m)}</span>
              <span className="launch-countdown__sub">min</span>
            </div>
            <div className="launch-countdown__sep">:</div>
            <div className="launch-countdown__unit">
              <span className="launch-countdown__num">{pad(countdown.s)}</span>
              <span className="launch-countdown__sub">sec</span>
            </div>
          </div>
          <WaitlistForm launched={countdown.launched} />
        </div>
      ) : (
        <div className="launch-countdown launch-countdown--live">
          <div className="launch-countdown__label">🏆 100-Player Lobbies Are Open!</div>
          <Link to="/lobby" className="hero__cta-primary launch-countdown__cta">
            Enter Lobby Now →
          </Link>
        </div>
      )}

      {/* ── Public waitlist (FOMO feed) ─────────── */}
      <PublicWaitlist />

      {/* ── Tournament request queue ─────────────── */}
      {!countdown.launched && <QueueWidget />}

      {/* ── Game picker ──────────────────────────── */}
      <section className="game-picker" id="tournaments">
        <div className="section-eyebrow">Choose Your Game</div>
        <h2 className="section-title">Pick a Tournament & Start Winning</h2>
        <p className="game-picker__sub">
          $1 USDC entry · 90% prize pool paid instantly on Polygon · Most kills wins
        </p>
        <div className="game-picker__grid game-picker__grid--4">
          {/* Apex card */}
          <div className="game-picker__card game-picker__card--apex">
            <div className="game-picker__card-glow" />
            <div className="game-picker__card-badge">Beta Live</div>
            <div className="game-picker__card-icon">
              <img src="/images/pathfinderapexenh.png" alt="Apex Legends" onError={e => { e.target.style.display='none'; }} />
            </div>
            <div className="game-picker__card-game">Apex Legends</div>
            <div className="game-picker__card-title">Most Kills<br/>Wins USDC</div>
            <ul className="game-picker__card-list">
              <li>Play one ranked match</li>
              <li>Highest kill count wins</li>
              <li>100 players per lobby</li>
              <li>30-min match window</li>
            </ul>
            <Link to="/lobby" className="game-picker__card-btn game-picker__card-btn--apex">
              Play Apex →
            </Link>
          </div>

          {/* Fortnite card */}
          <div className="game-picker__card game-picker__card--fn">
            <div className="game-picker__card-glow game-picker__card-glow--fn" />
            <div className="game-picker__card-badge game-picker__card-badge--fn">Beta Live</div>
            <div className="game-picker__card-icon">
              <img src="/images/fortnite/jonesy.png" alt="Fortnite" onError={e => { e.target.style.display='none'; }} />
            </div>
            <div className="game-picker__card-game game-picker__card-game--fn">Fortnite</div>
            <div className="game-picker__card-title">Most Kills<br/>Wins USDC</div>
            <ul className="game-picker__card-list">
              <li>Play any solo match</li>
              <li>Kills delta tracked automatically</li>
              <li>50 players · beta lobby</li>
              <li>30-min match window</li>
            </ul>
            <Link to="/fortnite" className="game-picker__card-btn game-picker__card-btn--fn">
              Play Fortnite →
            </Link>
          </div>

          {/* CS2 card */}
          <div className="game-picker__card game-picker__card--cs2">
            <div className="game-picker__card-glow game-picker__card-glow--cs2" />
            <div className="game-picker__card-badge game-picker__card-badge--cs2">Apr 27</div>
            <div className="game-picker__card-icon game-picker__card-icon--cs2">
              <img src="/images/CS2/cs2image1.png" alt="CS2 Premier" onError={e => { e.target.style.display='none'; }} />
            </div>
            <div className="game-picker__card-game game-picker__card-game--cs2">CS2 Premier</div>
            <div className="game-picker__card-title">Most Kills<br/>Wins USDC</div>
            <ul className="game-picker__card-list">
              <li>Play one Premier match</li>
              <li>Kills · K/D · Score tracked</li>
              <li>100 players per lobby</li>
              <li>60-min match window</li>
            </ul>
            <Link to="/cs2" className="game-picker__card-btn game-picker__card-btn--cs2">
              View CS2 Lobby →
            </Link>
          </div>

          {/* Tarkov card */}
          <div className="game-picker__card game-picker__card--tarkov">
            <div className="game-picker__card-glow game-picker__card-glow--tarkov" />
            <div className="game-picker__card-badge game-picker__card-badge--tarkov">Apr 27</div>
            <div className="game-picker__card-icon">
              <img src="/images/tarkov%20images/tarkovlogo.jpeg" alt="Tarkov Arena" onError={e => { e.target.style.display='none'; }} />
            </div>
            <div className="game-picker__card-game game-picker__card-game--tarkov">Tarkov Arena</div>
            <div className="game-picker__card-title">Last Hero<br/>Wins USDC</div>
            <ul className="game-picker__card-list">
              <li>Play one Last Hero match</li>
              <li>Kills · K/D · XP tracked</li>
              <li>20 players per lobby</li>
              <li>25-min match window</li>
            </ul>
            <Link to="/tarkov" className="game-picker__card-btn game-picker__card-btn--tarkov">
              View Tarkov Lobby →
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats strip ──────────────────────────── */}
      <div className="stats-strip">
        {[
          { val: '90%',     lbl: 'Goes to Winner'          },
          { val: '$1 USDC', lbl: '~$0.001 Gas on Polygon'  },
          { val: '4 Games', lbl: 'Apex · Fortnite · CS2 · Tarkov'  },
          { val: 'Instant', lbl: 'USDC Payout'             },
        ].map(s => (
          <div key={s.lbl} className="stats-strip__item">
            <div className="stats-strip__val">{s.val}</div>
            <div className="stats-strip__lbl">{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* ── Legends showcase ─────────────────────── */}
      <section className="legends">
        <div className="section-eyebrow">Pick Your Legend</div>
        <h2 className="section-title">Top Legends for Kill Count Tournaments</h2>
        <div className="legends__grid">
          {LEGENDS.map(l => (
            <div key={l.name} className="legend-card" style={{ '--legend-color': l.color }}>
              <div className="legend-card__img-wrap" style={{ background: l.fallbackGradient }}>
                <img
                  className="legend-card__img"
                  src={l.img}
                  alt={l.name}
                  onError={e => { e.target.style.display = 'none'; }}
                />
                <div className="legend-card__img-fade" />
              </div>
              <div className="legend-card__body">
                <div className="legend-card__role">{l.role}</div>
                <div className="legend-card__name">{l.name}</div>
                <div className="legend-card__tip">{l.tip}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ─────────────────────────── */}
      <section className="how">
        <div className="section-eyebrow">How It Works</div>
        <h2 className="section-title">Three Steps to Your First Win</h2>
        <div className="how__grid">
          {[
            {
              n: '01',
              icon: '🦊',
              title: 'Connect Your Wallet',
              desc: 'Link MetaMask or any Web3 wallet. Your wallet is your identity — no sign-up required.',
            },
            {
              n: '02',
              icon: '💵',
              title: 'Pay $1 USDC — That\'s It',
              desc: 'Join a tournament with just $1 USDC. Gas on Polygon is under $0.01 — swap a tiny bit of USDC → POL if needed. Funds locked in a smart contract.',
            },
            {
              n: '03',
              icon: '🎮',
              title: 'Play One 30-Minute Match',
              desc: 'Drop into one ranked Apex Legends match during the window. Most kills wins the pool.',
            },
            {
              n: '04',
              icon: '⚡',
              title: 'Get Paid On-Chain',
              desc: '90% of the prize pool hits your wallet instantly. No waiting. No disputes. No trust required.',
            },
          ].map(s => (
            <div key={s.n} className="how__card">
              <div className="how__step-icon">{s.icon}</div>
              <div className="how__step-num">STEP {s.n}</div>
              <div className="how__step-title">{s.title}</div>
              <div className="how__step-desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Fortnite promo ───────────────────────── */}
      <section className="fn-promo">
        <div className="fn-promo__top-bar">NEW GAME AVAILABLE</div>
        <div className="fn-promo__inner">
          <div className="fn-promo__left">
            <div className="fn-promo__eyebrow">⚡ Fortnite Tournaments</div>
            <h2 className="fn-promo__title">
              Most Kills<br />
              <span className="fn-promo__title-accent">Wins USDC</span>
            </h2>
            <p className="fn-promo__desc">
              Same platform. Same $1 entry. Same instant Polygon payout.
              Just drop, rack up kills in 30 minutes, and get paid. No shared match needed — pure solo performance.
            </p>
            <div className="fn-promo__badges">
              <span className="fn-promo__badge">🎯 Most kills wins</span>
              <span className="fn-promo__badge">⚡ Kills tracked automatically</span>
              <span className="fn-promo__badge">💵 Instant USDC payout</span>
            </div>
            <Link to="/fortnite" className="fn-promo__cta">
              Play Fortnite →
            </Link>
          </div>
          <div className="fn-promo__skins">
            {FN_SKINS.map(s => (
              <div key={s.name} className="fn-skin-card" style={{ '--fn-color': s.color }}>
                <div className="fn-skin-card__img-wrap" style={{ background: s.fallback }}>
                  <img
                    className="fn-skin-card__img"
                    src={s.img}
                    alt={s.name}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="fn-skin-card__body">
                  <div className="fn-skin-card__role">{s.role}</div>
                  <div className="fn-skin-card__name">{s.name}</div>
                  <div className="fn-skin-card__tip">{s.tip}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tarkov promo ─────────────────────────── */}
      <section className="tarkov-promo">
        <div className="tarkov-promo__top-bar">NEW GAME AVAILABLE</div>
        <div className="tarkov-promo__inner">
          <div className="tarkov-promo__left">
            <div className="tarkov-promo__eyebrow">☠️ Tarkov Arena Tournaments</div>
            <h2 className="tarkov-promo__title">
              Last Hero<br />
              <span className="tarkov-promo__title-accent">Wins USDC</span>
            </h2>
            <p className="tarkov-promo__desc">
              Intense, hardcore, and ruthless. Enter a 20-player lobby, rack up the most kills in a 25-minute
              Last Hero window, and claim 90% of the prize pool instantly on Polygon. Pure K/D skill — no luck.
            </p>
            <div className="tarkov-promo__badges">
              <span className="tarkov-promo__badge">☠️ Last Hero mode</span>
              <span className="tarkov-promo__badge">⚔️ Kills · K/D · XP tracked</span>
              <span className="tarkov-promo__badge">💵 Instant USDC payout</span>
            </div>
            <Link to="/tarkov" className="tarkov-promo__cta">
              Play Tarkov Arena →
            </Link>
          </div>
          <div className="tarkov-promo__right">
            <div className="tarkov-stat-card">
              <div className="tarkov-stat-card__icon">👥</div>
              <div>
                <div className="tarkov-stat-card__label">Players per lobby</div>
                <div className="tarkov-stat-card__val">20 max</div>
              </div>
            </div>
            <div className="tarkov-stat-card">
              <div className="tarkov-stat-card__icon">⏱️</div>
              <div>
                <div className="tarkov-stat-card__label">Match window</div>
                <div className="tarkov-stat-card__val">25 minutes</div>
              </div>
            </div>
            <div className="tarkov-stat-card">
              <div className="tarkov-stat-card__icon">🏆</div>
              <div>
                <div className="tarkov-stat-card__label">Winner takes</div>
                <div className="tarkov-stat-card__val">90% of pool</div>
              </div>
            </div>
            <div className="tarkov-stat-card">
              <div className="tarkov-stat-card__icon">🔗</div>
              <div>
                <div className="tarkov-stat-card__label">Stats verified via</div>
                <div className="tarkov-stat-card__val">player.tarkov.dev</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────── */}
      <section className="cta-banner">
        <div className="cta-banner__glow" />
        <div className="cta-banner__content">
          <h2 className="cta-banner__title">Ready to Compete?</h2>
          <p className="cta-banner__sub">Apex, Fortnite, CS2 &amp; Tarkov tournaments live. $1 entry. Instant USDC payout.</p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/lobby"    className="hero__cta-primary" style={{ fontSize: 16, padding: '16px 40px' }}>Play Apex →</Link>
            <Link to="/fortnite" className="fn-promo__cta"     style={{ fontSize: 16, padding: '16px 40px' }}>Play Fortnite →</Link>
            <Link to="/cs2"      className="hero__cta-secondary" style={{ fontSize: 16, padding: '16px 40px' }}>Play CS2 →</Link>
            <Link to="/tarkov"   className="tarkov-promo__cta" style={{ fontSize: 16, padding: '16px 40px' }}>Play Tarkov →</Link>
          </div>
        </div>
      </section>

      {/* ── Blog preview ─────────────────────────── */}
      <section className="blog-preview">
        <div className="section-eyebrow">From the Blog</div>
        <h2 className="section-title">Get the Edge</h2>
        <div className="blog-preview__grid">
          {ARTICLES.map(a => (
            <Link key={a.slug} to={`/blog/${a.slug}`} className="blog-card">
              <div className="blog-card__tag">{a.tag}</div>
              <div className="blog-card__title">{a.title}</div>
              <div className="blog-card__excerpt">{a.excerpt}</div>
              <div className="blog-card__read">Read article →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Footer ───────────────────────────────── */}
      <footer className="landing-footer">
        <div className="landing-footer__logo">EzEgames</div>
        <div className="landing-footer__links">
          <Link to="/lobby">Apex</Link>
          <Link to="/fortnite">Fortnite</Link>
          <Link to="/cs2">CS2</Link>
          <Link to="/tarkov">Tarkov</Link>
          <Link to="/results">Results</Link>
          <Link to="/leaderboard">Leaderboard</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/profile">Profile</Link>
          <a href="https://discord.gg/smgDF9jH" target="_blank" rel="noopener noreferrer">Discord</a>
          <a href="https://twitter.com/EZ_Egames" target="_blank" rel="noopener noreferrer">Twitter</a>
        </div>
        <div className="landing-footer__copy">
          © 2026 EZGAME. Built on Polygon. Not affiliated with EA or Respawn Entertainment.
        </div>
      </footer>
    </main>
  );
}
